import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useParams } from 'react-router-dom';

import { ApButtonSecondary } from '@alixpartners/ui-components';
import { useAppDispatch, useAppSelector } from '../../../state';
import {
  selectFilesState,
  uploadFiles,
  fetchAllFiles,
  refetchAllFiles,
  setUploadingFilesData,
  cancelUpload,
  setSelectedSection,
  fetchSessionsStatus,
  selectIsEmpty,
  fetchSchemas,
  selectError,
} from '../../../state/slices/filesSlice';
import { SidePanel } from '../../../components/SidePanel';
import { NavigationWrapper } from '../NavigationWrapper';
import { UploadProgress } from '../../../components/UploadProgress';
import {
  UploadedList,
  StagedList,
  OtherList,
  DeletedList,
} from '../../../components/UploadedFilesList';
import {
  PageWrapper,
  PageTitleBlock,
  PageTitle,
  PageSubtitle,
  ContentRow,
  Aside,
  MainCard,
  MainCardHeader,
  MainCardTitle,
  DropZoneBox,
  DropZoneTextContent,
  DropZonePrimary,
  DropZoneSecondary,
  UploadedDocsHeading,
  UploadInput,
  Loading,
  Error,
  ContinueToMappingButton,
} from './UploadMapping.styles';
import {
  IUploadingFilesData,
  ACCOUNTS_PAYABLE,
  ACCOUNTS_RECEIVABLES,
  INVENTORY,
  TFileSection,
  MAPPING,
  UPLOADED,
  STAGED,
  TFileStatus,
  IUserFilesBySection,
  DELETED,
} from '../../../types';
import { getFxrLink } from '../../../utils/common';
import {
  removeFile,
  removeSession,
  removeSessionFromFxr,
} from '../../../utils/api';
import { later } from '../../../utils/later';

const POLLING_INTERVAL = 10000;

const SECTION_HEADINGS: Record<TFileSection, string> = {
  'accounts-payable': 'Upload Accounts Payable documents',
  'accounts-receivables': 'Upload Accounts Receivables documents',
  inventory: 'Upload Inventory documents',
};

interface IUploadMappingProps {
  userName: string;
}

const isCorrectSection = (section: TFileSection) =>
  [ACCOUNTS_PAYABLE, ACCOUNTS_RECEIVABLES, INVENTORY].includes(section);

const getSectionicon = (
  section: TFileSection,
  userFiles: IUserFilesBySection,
  isFetching: boolean
) => {
  if (isFetching) {
    return undefined;
  } else {
    return Object.keys(userFiles[section][UPLOADED]).length === 0 &&
      Object.keys(userFiles[section][MAPPING]).length === 0 &&
      Object.keys(userFiles[section][STAGED]).length > 0
      ? 'done_all'
      : Object.keys(userFiles[section][MAPPING]).length !== 0
      ? 'pause'
      : 'cloud_upload_outlined';
  }
};

export const UploadMapping: React.FC<IUploadMappingProps> = ({ userName }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { section } = useParams();

  const {
    isUploading,
    isFetching,
    uploadingFilesCount,
    selectedSection,
    userFiles,
    otherFiles,
    malformedRowsUrlMap,
    sessionExpirationDatesMap,
  } = useAppSelector(selectFilesState);

  const isEmpty = useAppSelector(selectIsEmpty);
  const error = useAppSelector(selectError);

  const uploadedSessionEntries = Object.entries(
    userFiles[selectedSection]?.[UPLOADED] ?? {}
  );
  const hasUploadedSessions = uploadedSessionEntries.length > 0;

  const [uploadRequest, setUploadRequest] = useState<any>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  useEffect(() => {
    let stopTick = false;

    dispatch(fetchAllFiles());
    dispatch(fetchSchemas());

    const tick = async () => {
      if (stopTick) {
        return;
      }
      await dispatch(fetchSessionsStatus());
      await dispatch(refetchAllFiles());

      await later(POLLING_INTERVAL);

      tick();
    };

    tick();
    return () => {
      stopTick = true;
    };
  }, []);

  useEffect(() => {
    const currentSection = section as TFileSection;

    if (!currentSection || !isCorrectSection(currentSection)) {
      navigate(`../${ACCOUNTS_PAYABLE}`);
    } else {
      dispatch(setSelectedSection(currentSection));
    }
  }, [section]);

  const MAX_FILE_SIZE = 15 * 1024 * 1024;

  const onDrop = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
      if (oversized.length > 0) {
        setFileSizeError(
          `File${oversized.length > 1 ? 's' : ''} over 15MB cannot be uploaded.`
        );
        return;
      }
      setFileSizeError(null);
      handleFileUpload(files);
    },
    [selectedSection]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    disabled: isUploading,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.csv',
      ],
    },
  });

  const handleOnSelect = (option: TFileSection) => {
    dispatch(setSelectedSection(option));
    navigate(`../${option}`);
  };

  const handleOnUploadCancelClick = () => {
    if (uploadRequest) {
      dispatch(cancelUpload());
      uploadRequest.abort();
    }
  };

  const handleOpenMapper = (sessionId: string, status: TFileStatus) => {
    if (status === UPLOADED) {
      setTimeout(() => {
        dispatch(fetchSessionsStatus());
      }, 10000);
    }

    window.open(getFxrLink(selectedSection, sessionId), '_self');
  };

  const handleContinueToMapping = () => {
    const [sessionId, files] = uploadedSessionEntries[0] ?? [];
    if (!sessionId || !files) return;
    handleOpenMapper(sessionId, UPLOADED);
  };

  const [handleDeleteMappingSession] = useDeleteHook(
    async (sessionId: string) => {
      await removeSession(sessionId);
      await removeSessionFromFxr(sessionId);
    },
    () => {
      dispatch(refetchAllFiles());
    }
  );

  const [handleDeleteExpiredMappingSession] = useDeleteHook(
    async (sessionId: string) => {
      await removeSession(sessionId);
    },
    () => {
      dispatch(refetchAllFiles());
    }
  );

  const [handleDeleteFile] = useDeleteHook(
    async (fileId: string) => {
      await removeFile(fileId);
    },
    () => {
      dispatch(refetchAllFiles());
    }
  );

  const handleFileUpload = (
    files: File[],
    data: IUploadingFilesData = { userName }
  ) => {
    dispatch(setUploadingFilesData({ ...data, userName }));
    const uploadThunkPromise = dispatch(uploadFiles(files));
    setUploadRequest(uploadThunkPromise);
  };

  return (
    <>
      <NavigationWrapper
        onFileUploadClick={open}
        isUploadDisabled={isUploading}
      />
      <PageWrapper>
        <PageTitleBlock>
          <PageTitle>Upload your files</PageTitle>
          <PageSubtitle>
            Choose the file type from the left menu, then upload one or multiple
            files. Files are grouped together - to make changes, you’ll need to
            remove and reupload the full set. <br />
            Make sure all uploaded files use the same structure and column
            layout (schema). Uploading files with different formats will result
            in processing errors or inaccurate mapping results.
          </PageSubtitle>
        </PageTitleBlock>
        <ContentRow>
          <Aside>
            <SidePanel
              options={[
                {
                  title: 'Accounts Payable',
                  value: ACCOUNTS_PAYABLE,
                  iconName: getSectionicon(
                    ACCOUNTS_PAYABLE,
                    userFiles,
                    isFetching
                  ),
                },
                {
                  title: 'Accounts Receivables',
                  value: ACCOUNTS_RECEIVABLES,
                  iconName: getSectionicon(
                    ACCOUNTS_RECEIVABLES,
                    userFiles,
                    isFetching
                  ),
                },
                {
                  title: 'Inventory',
                  value: INVENTORY,
                  iconName: getSectionicon(INVENTORY, userFiles, isFetching),
                },
              ]}
              onSelect={handleOnSelect}
              selected={selectedSection}
            />
          </Aside>
          <MainCard {...getRootProps()}>
            <UploadInput {...getInputProps()} />
            {isFetching ? (
              <Loading />
            ) : (
              <>
                <MainCardHeader>
                  <MainCardTitle>
                    {SECTION_HEADINGS[selectedSection]}
                  </MainCardTitle>
                  <ContinueToMappingButton
                    disabled={!hasUploadedSessions || isUploading}
                    onClick={handleContinueToMapping}
                  >
                    Continue to mapping
                  </ContinueToMappingButton>
                </MainCardHeader>
                <DropZoneBox isDragActive={isDragActive}>
                  <ApButtonSecondary
                    small
                    onClick={open}
                    disabled={isUploading}
                  >
                    Browse files
                  </ApButtonSecondary>
                  <DropZoneTextContent>
                    <DropZonePrimary>or drag multiple files</DropZonePrimary>
                    <DropZoneSecondary>csv files up to 15MB</DropZoneSecondary>
                  </DropZoneTextContent>
                </DropZoneBox>
                {error && <Error>{error}</Error>}
                {fileSizeError && <Error>{fileSizeError}</Error>}
                {isUploading && (
                  <UploadProgress
                    filesCount={uploadingFilesCount}
                    onCancelClick={handleOnUploadCancelClick}
                  />
                )}
                {!isEmpty && (
                  <>
                    {(Object.keys(userFiles[selectedSection][UPLOADED]).length >
                      0 ||
                      Object.keys(userFiles[selectedSection][DELETED]).length >
                        0 ||
                      Object.keys(userFiles[selectedSection][STAGED]).length >
                        0 ||
                      otherFiles[selectedSection].length > 0) && (
                      <UploadedDocsHeading>
                        Uploaded documents
                      </UploadedDocsHeading>
                    )}

                    <DeletedList
                      files={userFiles[selectedSection][DELETED]}
                      onDeleteMappingSession={handleDeleteExpiredMappingSession}
                      selectedSection={selectedSection}
                      sessionExpirationDatesMap={sessionExpirationDatesMap}
                    />
                    <UploadedList
                      files={userFiles[selectedSection][UPLOADED]}
                      onOpenMapper={handleOpenMapper}
                      onDeleteMappingSession={handleDeleteMappingSession}
                      selectedSection={selectedSection}
                      sessionExpirationDatesMap={sessionExpirationDatesMap}
                    />
                    {otherFiles[selectedSection].length > 0 && (
                      <OtherList
                        files={otherFiles[selectedSection]}
                        key={selectedSection}
                        onDeleteFile={handleDeleteFile}
                      />
                    )}
                    <StagedList
                      files={userFiles[selectedSection][STAGED]}
                      malformedRowsUrlMap={malformedRowsUrlMap}
                      onDeleteFile={handleDeleteFile}
                    />
                  </>
                )}
              </>
            )}
          </MainCard>
        </ContentRow>
      </PageWrapper>
    </>
  );
};

export const useDeleteHook = (
  deleteAction: (id: string) => Promise<any>,
  refreshAction: Function
) => {
  const [deletingIds, setDeletingIds] = useState<string[]>([]);

  const handleDelete = async (id: string) => {
    setDeletingIds((deletingIds) => [...deletingIds, id]);

    if (deletingIds.includes(id)) return;

    try {
      await deleteAction(id);
    } finally {
      setDeletingIds((deletingIds) =>
        deletingIds.filter((deletingId) => id !== deletingId)
      );

      await refreshAction();
    }
  };

  return [handleDelete, deletingIds] as const;
};
